import { useEffect, useState } from "react";
import { trackPageview } from "../../utils/analyticsUtils";
import { Helmet } from "react-helmet";
import { LuMenu } from "react-icons/lu";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import {
  SideMenuBehavior,
  useIsSideMenuOpen,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "../welcome/components/WelcomeAppBar";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";
import { BlogPostCard } from "./components/BlogPostCard";

interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string;
  dateFolder: string;
}

export function BlogListPage() {
  const [posts, setPosts] = useState<BlogPostMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();
  const { open: openSideMenu, close: closeSideMenu } = useSideMenuActions();
  const isSideMenuOpen = useIsSideMenuOpen();
  const sideMenuBehavior = useSideMenuBehavior();

  useEffect(() => {
    trackPageview("Blog");
  }, []);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch("/blog/index.json");
        if (!response.ok) {
          throw new Error("Failed to load blog posts");
        }
        const data = await response.json();
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Blog | Darvin - AI-Powered Mobile App Builder</title>
        <meta
          name="description"
          content="Read the latest articles about no-code mobile app development, AI-powered app creation, and tips for building Flutter apps with Darvin."
        />
        <meta property="og:title" content="Blog | Darvin" />
        <meta
          property="og:description"
          content="Read the latest articles about no-code mobile app development, AI-powered app creation, and tips for building Flutter apps with Darvin."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://darvin.dev/blog" />
      </Helmet>

      <div className="min-h-screen flex">
        {isUserSignedIn && !isCompactView && <SideMenuPanel />}
        <div
          className={`${
            isUserSignedIn &&
            !isCompactView &&
            (sideMenuBehavior === SideMenuBehavior.PINNED
              ? "pl-[320px]"
              : "pl-[56px]")
          } flex-1 flex flex-col transition-[padding] duration-300 relative`}
        >
          {/* Header with clean gradient background */}
          <div className="flex flex-col relative bg-gradient-hero-dark">
            <div className="flex flex-row items-center w-full relative z-10">
              {isUserSignedIn && isCompactView && (
                <div className="pl-4">
                  <LuMenu
                    size={34}
                    onClick={isSideMenuOpen ? closeSideMenu : openSideMenu}
                    className="cursor-pointer text-white"
                  />
                </div>
              )}
              <WelcomeAppBar />
            </div>

            <div className="flex justify-center items-center px-8 pt-8 pb-16 md:pb-20 relative z-10">
              <div className="w-full max-w-[1300px] text-center">
                <h1 className="text-[42px] lg:text-[48px] font-bold text-white mb-4">
                  Darvin Blog
                </h1>
                <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto">
                  Tutorials, guides, and insights on building mobile apps with
                  AI, prompt engineering, and no-code development
                </p>
              </div>
            </div>

            {/* Fade transition overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-b from-transparent via-dark-surface-primary/60 to-dark-surface-primary z-10" />
          </div>

          {/* Blog Posts Grid */}
          <div
            className="flex-1 flex flex-col
            bg-dark-surface-primary px-8"
          >
            <div className="w-full max-w-[1300px] mx-auto pt-8 pb-16 md:pt-12 md:pb-24">
              {loading && (
                <div className="text-center py-12">
                  <p className="text-dark-text-secondary">
                    Loading posts...
                  </p>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="text-red-400">
                    Error: {error}
                  </p>
                </div>
              )}

              {!loading && !error && posts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-dark-text-secondary">
                    No blog posts available yet.
                  </p>
                </div>
              )}

              {!loading && !error && posts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <BlogPostCard
                      key={post.slug}
                      slug={post.slug}
                      title={post.title}
                      excerpt={post.excerpt}
                      coverImage={post.coverImage}
                      date={post.date}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <WelcomeFooter />
        </div>
      </div>
    </>
  );
}
